import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { z } from "zod";
import { ArrowRight, Download, FileText, Loader2, ShieldCheck, Sparkles } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "@/hooks/use-toast";
import { useRoles } from "@/hooks/useRole";

const FormSchema = z.object({
  name: z.string().trim().min(1, "Your name is required").max(120),
  email: z.string().trim().email("Enter a valid email").max(255),
  org: z.string().trim().max(160).optional(),
  role: z.string().trim().max(120).optional(),
  consent_marketing: z.boolean().optional(),
});

type FormState = z.infer<typeof FormSchema>;

export default function IntelReport() {
  const { slug = "alabama-capital-flow-q1-2026" } = useParams();

  const { data: report, isLoading } = useQuery({
    queryKey: ["intel_report", slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("intelligence_reports")
        .select("slug, title, abstract, quarter_label, sector, county_scope, page_count, is_published, published_at")
        .eq("slug", slug)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });

  const { isSuperAdmin } = useRoles();
  const [drafting, setDrafting] = useState(false);
  const triggerDraft = async () => {
    setDrafting(true);
    try {
      const { data, error } = await supabase.functions.invoke("draft-intel-report", {
        body: { slug, publish: true },
      });
      if (error) throw error;
      const d = data as { ok?: boolean; page_count?: number; error?: string };
      if (!d?.ok) throw new Error(d?.error ?? "Draft failed");
      toast({ title: "Report drafted", description: `Uploaded ${d.page_count ?? 0}-page PDF and published.` });
      window.location.reload();
    } catch (err) {
      toast({
        title: "Draft failed",
        description: err instanceof Error ? err.message : "See edge function logs",
        variant: "destructive",
      });
    } finally {
      setDrafting(false);
    }
  };

  const [form, setForm] = useState<FormState>({
    name: "",
    email: "",
    org: "",
    role: "",
    consent_marketing: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);

  useEffect(() => {
    document.title = report?.title
      ? `${report.title} · Intelligence Brief`
      : "Intelligence Brief · The Emerging Market";
  }, [report?.title]);

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = FormSchema.safeParse(form);
    if (!parsed.success) {
      toast({
        title: "Please check the form",
        description: parsed.error.issues[0]?.message ?? "Invalid input",
        variant: "destructive",
      });
      return;
    }
    setSubmitting(true);
    try {
      const { data, error } = await supabase.functions.invoke("intel-report-request", {
        body: { ...parsed.data, slug, source: "intel_report_gate" },
      });
      if (error) throw error;
      const d = data as { download_url?: string; error?: string };
      if (!d?.download_url) throw new Error(d?.error ?? "No download link returned");
      setDownloadUrl(d.download_url);
      toast({ title: "Report ready", description: "Your download link is below." });
    } catch (err) {
      toast({
        title: "Could not send the report",
        description: err instanceof Error ? err.message : "Please try again.",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main className="container max-w-4xl mx-auto px-4 py-12 md:py-16">
        {isLoading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : !report ? (
          <div className="text-center py-24">
            <h1 className="font-display text-2xl font-semibold">Report not found</h1>
            <p className="mt-2 text-muted-foreground">
              Check <Link to="/for-funders" className="text-primary underline">/for-funders</Link> for available briefings.
            </p>
          </div>
        ) : (
          <>
            <div className="mb-8">
              <Badge variant="outline" className="mb-3 border-primary/30 text-primary">
                <Sparkles className="h-3 w-3 mr-1" /> Intelligence Brief · {report.quarter_label}
              </Badge>
              <h1 className="font-display text-3xl md:text-4xl font-bold text-foreground">
                {report.title}
              </h1>
              <p className="mt-4 text-muted-foreground text-lg">{report.abstract}</p>
              <div className="mt-4 flex flex-wrap gap-2 text-xs text-muted-foreground">
                {report.sector && <span className="rounded-full bg-muted px-2 py-1">Sector: {report.sector}</span>}
                {report.county_scope && <span className="rounded-full bg-muted px-2 py-1">Scope: {report.county_scope}</span>}
                {report.page_count && <span className="rounded-full bg-muted px-2 py-1">{report.page_count} pages</span>}
                <span className="rounded-full bg-muted px-2 py-1">AI-drafted · human-reviewed</span>
              </div>
            </div>

            {isSuperAdmin && (
              <div className="mb-6 rounded-xl border border-dashed border-primary/40 bg-primary/5 p-4 flex items-center justify-between gap-3">
                <div className="text-xs text-muted-foreground">
                  Admin: regenerate this brief with the latest data + AI draft, upload the PDF, and publish.
                </div>
                <Button size="sm" variant="outline" disabled={drafting} onClick={triggerDraft}>
                  {drafting ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Sparkles className="h-3 w-3 mr-1" />}
                  {drafting ? "Drafting…" : "Draft & publish"}
                </Button>
              </div>
            )}

            <section className="rounded-2xl border border-border bg-card p-6 md:p-8 mb-8">
              <div className="flex items-center gap-2 mb-4">
                <FileText className="h-5 w-5 text-primary" />
                <h2 className="font-display text-xl font-semibold">What's inside</h2>
              </div>
              <ul className="grid md:grid-cols-2 gap-3 text-sm text-foreground/90">
                <li>• Executive summary of Alabama's Q1 capital flow</li>
                <li>• Funder 990 flows into Alabama recipients</li>
                <li>• Sector spotlight — Health, Community, Education</li>
                <li>• Legislation watch (LegiScan-tracked bills)</li>
                <li>• Outlook for the next quarter</li>
                <li>• Methodology & data sources</li>
              </ul>
            </section>

            {!report.is_published ? (
              <div className="rounded-2xl border border-amber-500/30 bg-amber-500/5 p-6 text-sm">
                <p className="font-medium text-foreground">Publishing soon.</p>
                <p className="text-muted-foreground mt-1">
                  This briefing is in final editorial review. Sign up on the <Link to="/for-funders" className="text-primary underline">funders page</Link> to be notified the day it drops.
                </p>
              </div>
            ) : downloadUrl ? (
              <div className="rounded-2xl border border-primary/30 bg-primary/5 p-6 md:p-8 text-center">
                <ShieldCheck className="h-8 w-8 text-primary mx-auto mb-3" />
                <h2 className="font-display text-xl font-semibold mb-2">Your report is ready</h2>
                <p className="text-sm text-muted-foreground mb-5">Download link is valid for 1 hour.</p>
                <Button asChild size="lg">
                  <a href={downloadUrl} target="_blank" rel="noopener noreferrer">
                    <Download className="h-4 w-4 mr-2" /> Download PDF
                  </a>
                </Button>
              </div>
            ) : (
              <form
                onSubmit={onSubmit}
                className="rounded-2xl border border-border bg-card p-6 md:p-8 space-y-4"
              >
                <div>
                  <h2 className="font-display text-xl font-semibold">Get the full report</h2>
                  <p className="text-sm text-muted-foreground mt-1">
                    Free download. We'll email you when the next quarterly brief drops.
                  </p>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name">Your name *</Label>
                    <Input id="name" required value={form.name} onChange={(e) => set("name", e.target.value)} />
                  </div>
                  <div>
                    <Label htmlFor="email">Work email *</Label>
                    <Input id="email" type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} />
                  </div>
                  <div>
                    <Label htmlFor="org">Organization</Label>
                    <Input id="org" value={form.org ?? ""} onChange={(e) => set("org", e.target.value)} />
                  </div>
                  <div>
                    <Label htmlFor="role">Role</Label>
                    <Input id="role" placeholder="Program officer, analyst, director…" value={form.role ?? ""} onChange={(e) => set("role", e.target.value)} />
                  </div>
                </div>
                <div className="flex items-start gap-2 pt-2">
                  <Checkbox
                    id="consent_marketing"
                    checked={form.consent_marketing ?? false}
                    onCheckedChange={(c) => set("consent_marketing", c === true)}
                  />
                  <Label htmlFor="consent_marketing" className="text-xs text-muted-foreground leading-snug font-normal">
                    Email me the next quarterly briefing and occasional Intelligence updates. Unsubscribe anytime. See our{" "}
                    <Link to="/privacy" className="text-primary underline">Privacy Policy</Link>.
                  </Label>
                </div>
                <Button type="submit" size="lg" disabled={submitting} className="w-full md:w-auto">
                  {submitting ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Preparing…</>
                  ) : (
                    <>Email me the PDF <ArrowRight className="h-4 w-4 ml-1" /></>
                  )}
                </Button>
              </form>
            )}
          </>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}